import React from 'react';
import { motion } from 'framer-motion';
import { Landmark, Briefcase, TrendingUp, CheckCircle2, ArrowRight } from 'lucide-react';
export const Services = () => {
  const practices = [{
    title: 'Tax & Accounting',
    icon: <Landmark size={40} data-id="element-484" />,
    color: 'bg-brand-navy',
    services: [{
      name: 'Individual Tax Preparation',
      desc: 'Comprehensive tax compliance and filing for high-net-worth individuals.'
    }, {
      name: 'Business Tax Preparation',
      desc: 'Strategic corporate tax filing and compliance for SMEs and large entities.'
    }, {
      name: 'Tax Planning',
      desc: 'Proactive strategies to optimize tax positions and preserve wealth.'
    }, {
      name: 'Bookkeeping & Payroll',
      desc: 'Meticulous financial record-keeping and payroll management.'
    }, {
      name: 'Financial Statements',
      desc: 'Preparation of institutional-quality financial reports and audits.'
    }, {
      name: 'Outsourced CFO Services',
      desc: 'Strategic financial leadership for growing businesses.'
    }]
  }, {
    title: 'Strategic Advisory',
    icon: <Briefcase size={40} data-id="element-485" />,
    color: 'bg-brand-emerald',
    services: [{
      name: 'Buy-Side Advisory',
      desc: 'Expert guidance through the acquisition process, from identification to closing.'
    }, {
      name: 'Deal Screening',
      desc: 'Rigorous analysis and due diligence for potential investment opportunities.'
    }, {
      name: 'Business Acquisitions',
      desc: 'Strategic support for expanding your portfolio through targeted acquisitions.'
    }, {
      name: 'Strategic Partnerships',
      desc: 'Facilitating high-value alliances and joint ventures.'
    }, {
      name: 'International Expansion',
      desc: 'Navigating the complexities of entering new global markets.'
    }, {
      name: 'Business Process Improvement',
      desc: 'Optimizing operations for maximum efficiency and profitability.'
    }]
  }, {
    title: 'Consilium Capital',
    icon: <TrendingUp size={40} data-id="element-486" />,
    color: 'bg-brand-gold',
    services: [{
      name: 'Growth Capital',
      desc: 'Tailored funding solutions to fuel business expansion and innovation.'
    }, {
      name: 'Transformation Capital',
      desc: 'Strategic investment to drive organizational change and revitalization.'
    }, {
      name: 'Portfolio Advisory',
      desc: 'Active management and strategic guidance for investment portfolios.'
    }, {
      name: 'Investment Strategy',
      desc: 'Developing robust frameworks for long-term capital appreciation.'
    }]
  }];
  return <main className="pt-24" data-id="element-487">
      {/* Hero */}
      <section className="bg-brand-navy py-24 text-white relative overflow-hidden" data-id="element-488">
        <div className="absolute inset-0 opacity-10" data-id="element-489">
          <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/patterns/generated-images/27559d20-56df-4ace-a32a-f1c2ddb34050.jpg')] bg-cover bg-center" data-id="element-490" />
        </div>
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-491">
          <span className="text-brand-gold font-bold tracking-widest uppercase text-sm mb-6 block" data-id="element-492">
            Our Expertise
          </span>
          <h1 className="text-5xl md:text-7xl font-display mb-8" data-id="element-493">
            Comprehensive <br data-id="element-494" />
            <span className="italic" data-id="element-495">Financial</span> Solutions.
          </h1>
          <p className="text-xl text-white/70 max-w-2xl leading-relaxed" data-id="element-496">
            We provide a multidisciplinary approach to financial success,
            combining technical mastery with strategic insight across three core
            practices.
          </p>
        </motion.div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white" data-id="element-497">
        <div className="max-w-7xl mx-auto px-6" data-id="element-498">
          <div className="space-y-32" data-id="element-499">
            {practices.map((practice, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 40
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8
          }} className="grid lg:grid-cols-3 gap-16" data-id="element-500">
                <div className="lg:col-span-1" data-id="element-501">
                  <div className={`inline-flex p-5 rounded-2xl text-white mb-8 ${practice.color}`} data-id="element-502">
                    {practice.icon}
                  </div>
                  <h2 className="text-4xl font-display text-brand-navy mb-6" data-id="element-503">
                    {practice.title}
                  </h2>
                  <p className="text-brand-charcoal/60 leading-relaxed mb-8" data-id="element-504">
                    Delivering institutional-quality solutions tailored to the
                    unique needs of entrepreneurs, investors, and corporate
                    leaders.
                  </p>
                  <button className="bg-brand-navy text-white px-8 py-4 rounded-full font-bold text-sm uppercase tracking-widest hover:bg-brand-gold transition-all flex items-center gap-3" data-id="element-505">
                    Get Started <ArrowRight size={18} data-id="element-506" />
                  </button>
                </div>
                <div className="lg:col-span-2 grid md:grid-cols-2 gap-8" data-id="element-507">
                  {practice.services.map((service, sIdx) => <motion.div key={sIdx} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: sIdx * 0.1
              }} whileHover={{
                y: -5
              }} className="p-8 rounded-2xl bg-brand-warm-white border border-brand-navy/5 hover:border-brand-gold transition-all group cursor-default" data-id="element-508">
                      <div className="text-brand-gold mb-4" data-id="element-509">
                        <CheckCircle2 size={24} data-id="element-510" />
                      </div>
                      <h4 className="text-xl font-heading font-bold text-brand-navy mb-3" data-id="element-511">
                        {service.name}
                      </h4>
                      <p className="text-sm text-brand-charcoal/60 leading-relaxed" data-id="element-512">
                        {service.desc}
                      </p>
                    </motion.div>)}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-brand-navy text-white overflow-hidden" data-id="element-513">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="max-w-4xl mx-auto px-6 text-center" data-id="element-514">
          <h2 className="text-4xl md:text-5xl font-display mb-8" data-id="element-515">
            Ready to navigate your <br data-id="element-516" />
            <span className="italic text-brand-gold" data-id="element-517">financial future?</span>
          </h2>
          <p className="text-white/60 text-lg mb-12" data-id="element-518">
            Our partners are available for a strategic consultation to discuss
            your specific goals.
          </p>
          <button className="bg-brand-gold text-white px-10 py-5 text-sm font-bold uppercase tracking-widest hover:bg-white hover:text-brand-navy transition-all" data-id="element-519">
            Schedule a Consultation
          </button>
        </motion.div>
      </section>
    </main>;
};